import type {
  ApiResponse,
  ProjectLabel,
  CreateLabelRequest,
  AddLabelToNodeRequest,
} from '@bandos/shared'
import { HttpClient } from '../api/client'
import { TokenManager } from '../auth/tokenManager'

/**
 * Type guard for HTTP errors with response data
 */
interface HttpErrorResponse {
  response?: {
    data?: { message?: string }
    status?: number
  }
  message?: string
}

function getErrorInfo(error: unknown): { message: string; status?: number } {
  const httpError = error as HttpErrorResponse
  const message =
    httpError.response?.data?.message ??
    httpError.message ??
    (error instanceof Error ? error.message : 'Unknown error')
  return { message, status: httpError.response?.status }
}

/**
 * Label service for managing project labels and node label assignments
 */

/**
 * Get all labels for a mindmap
 */
export async function getProjectLabels(
  mindmapId: string
): Promise<ApiResponse<{ labels: ProjectLabel[] }>> {
  try {
    const headers = await TokenManager.getAuthHeaders()
    const response = await HttpClient.get<{ labels: ProjectLabel[] }>(
      `/mindmaps/${mindmapId}/labels`,
      headers
    )
    return { data: response.data, error: null }
  } catch (error: unknown) {
    const errorInfo = getErrorInfo(error)
    return {
      data: null,
      error: {
        message: errorInfo.message || 'Failed to fetch project labels',
        status: errorInfo.status,
      },
    }
  }
}

/**
 * Create a new label for a mindmap
 */
export async function createProjectLabel(
  mindmapId: string,
  labelText: string,
  color: string
): Promise<ApiResponse<ProjectLabel>> {
  try {
    const headers = await TokenManager.getAuthHeaders()
    const body: CreateLabelRequest = {
      label_text: labelText,
      color,
    }
    const response = await HttpClient.post<ProjectLabel>(
      `/mindmaps/${mindmapId}/labels`,
      body,
      headers
    )
    return { data: response.data, error: null }
  } catch (error: unknown) {
    const errorInfo = getErrorInfo(error)
    return {
      data: null,
      error: {
        message: errorInfo.message || 'Failed to create label',
        status: errorInfo.status,
      },
    }
  }
}

/**
 * Get labels assigned to a specific node
 */
export async function getNodeLabels(
  mindmapId: string,
  nodeId: string
): Promise<ApiResponse<{ labels: ProjectLabel[] }>> {
  try {
    const headers = await TokenManager.getAuthHeaders()
    const response = await HttpClient.get<{ labels: ProjectLabel[] }>(
      `/mindmaps/${mindmapId}/nodes/${nodeId}/labels`,
      headers
    )
    return { data: response.data, error: null }
  } catch (error: unknown) {
    const errorInfo = getErrorInfo(error)
    return {
      data: null,
      error: {
        message: errorInfo.message || 'Failed to fetch node labels',
        status: errorInfo.status,
      },
    }
  }
}

/**
 * Add a label to a node
 */
export async function addLabelToNode(
  mindmapId: string,
  nodeId: string,
  labelId: string
): Promise<ApiResponse<{ success: boolean }>> {
  const headers = await TokenManager.getAuthHeaders()
  const body: AddLabelToNodeRequest = {
    label_id: labelId,
  }
  const response = await HttpClient.post<{ success: boolean }>(
    `/mindmaps/${mindmapId}/nodes/${nodeId}/labels`,
    body,
    headers
  )
  return response
}

/**
 * Remove a label from a node
 */
export async function removeLabelFromNode(
  mindmapId: string,
  nodeId: string,
  labelId: string
): Promise<ApiResponse<{ success: boolean }>> {
  try {
    const headers = await TokenManager.getAuthHeaders()
    const response = await HttpClient.delete<{ success: boolean }>(
      `/mindmaps/${mindmapId}/nodes/${nodeId}/labels/${labelId}`,
      headers
    )
    return { data: response.data, error: null }
  } catch (error: unknown) {
    const errorInfo = getErrorInfo(error)
    return {
      data: null,
      error: {
        message: errorInfo.message || 'Failed to remove label from node',
        status: errorInfo.status,
      },
    }
  }
}

/**
 * Delete a project label (removes it from all nodes)
 */
export async function deleteProjectLabel(
  mindmapId: string,
  labelId: string
): Promise<ApiResponse<{ success: boolean }>> {
  try {
    const headers = await TokenManager.getAuthHeaders()
    const response = await HttpClient.delete<{ success: boolean }>(
      `/mindmaps/${mindmapId}/labels/${labelId}`,
      headers
    )
    return { data: response.data, error: null }
  } catch (error: unknown) {
    const errorInfo = getErrorInfo(error)
    return {
      data: null,
      error: {
        message: errorInfo.message || 'Failed to delete project label',
        status: errorInfo.status,
      },
    }
  }
}

// ============================================================================
// PUBLIC ENDPOINTS (no authentication required, only for public mindmaps)
// ============================================================================

/**
 * Get all labels for a public mindmap (no authentication required)
 */
export async function getPublicProjectLabels(
  mindmapId: string
): Promise<ApiResponse<{ labels: ProjectLabel[] }>> {
  try {
    const response = await HttpClient.get<{ labels: ProjectLabel[] }>(
      `/public/mindmaps/${mindmapId}/labels`
    )
    return { data: response.data, error: null }
  } catch (error: unknown) {
    const errorInfo = getErrorInfo(error)
    return {
      data: null,
      error: {
        message: errorInfo.message || 'Failed to fetch project labels',
        status: errorInfo.status,
      },
    }
  }
}

/**
 * Get all node labels for a public mindmap (bulk operation, no authentication required)
 */
export async function getPublicAllNodeLabels(
  mindmapId: string
): Promise<ApiResponse<{ nodeLabels: Array<{ nodeId: string; labels: ProjectLabel[] }> }>> {
  try {
    const response = await HttpClient.get<{
      nodeLabels: Array<{ nodeId: string; labels: ProjectLabel[] }>
    }>(`/public/mindmaps/${mindmapId}/node-labels`)
    return { data: response.data, error: null }
  } catch (error: unknown) {
    const errorInfo = getErrorInfo(error)
    return {
      data: null,
      error: {
        message: errorInfo.message || 'Failed to fetch node labels',
        status: errorInfo.status,
      },
    }
  }
}
